import {Box, Inline, Stack, View, Text} from "@unibuddy/patron";
import {useAnalytics} from "@unibuddy/tracking";
import {
    SpaceType,
    useSpaces,
} from "ubcommunity-shared/src/StudentApp/SpacesProvider/SpacesProvider";
import {useAsyncLocalStorage} from "ubcommunity-shared/src/Auth/AuthProvider/useAsyncLocalStorage";
import {LocalStorageNames, TrackEvents} from "../constants";
import {HomeBarContent, HomeCommunityTitle, HomeUniversityName} from "./HomeBarContent";
import {HomeGradient} from "./HomeGradient";
import {HomeCustomButton} from "./HomeCustomButton";

export const enum SelectionDirection {
    NEXT = "next",
    PREVIOUS = "previous",
}

function SingleSpaceHeader() {
    const {selectedSpace} = useSpaces();

    return (
        <Stack space="small">
            <Box display="flex" flex={1}>
                <HomeUniversityName selectedSpace={selectedSpace} />
            </Box>
            {selectedSpace?.title ? <HomeCommunityTitle selectedSpace={selectedSpace} /> : null}
        </Stack>
    );
}

function MultiSpaceHeader() {
    const {spaces, selectedSpace, setSelectedSpace, selectCommunitySpace} = useSpaces();
    const {trackEvent} = useAnalytics();
    const currentIndex = spaces.findIndex((space) => space.id === selectedSpace?.id);
    const {value: showSpaceChanger, setValue: setShowSpaceChanger} = useAsyncLocalStorage(
        LocalStorageNames.SHOW_SPACE_CHANGER,
        true,
    );

    const handleSpaceSelection = (direction: SelectionDirection) => {
        const nextIndex =
            direction === SelectionDirection.NEXT ? currentIndex + 1 : currentIndex - 1;

        const spaceToSelect = spaces[(nextIndex + spaces.length) % spaces.length];

        setSelectedSpace(spaceToSelect);

        if (spaceToSelect.type === SpaceType.COMMUNITY) {
            selectCommunitySpace(spaceToSelect.id);
        }

        trackEvent(TrackEvents.SPACE_SELECTED, {
            id: spaceToSelect.id,
            name: spaceToSelect.name,
            type: spaceToSelect.type,
            slug: spaceToSelect.slug,
            direction,
        });
    };

    const handleSpaceChangerToggle = (visibility: boolean) => {
        setShowSpaceChanger(visibility);
        trackEvent(TrackEvents.SPACE_CHANGER_TOGGLED, {
            showSpaceChanger: visibility,
        });
    };
    return showSpaceChanger ? (
        <Stack space="small-medium">
            <HomeBarContent
                selectedSpace={selectedSpace}
                handleSpaceChangerToggle={handleSpaceChangerToggle}
                handleSpaceSelection={handleSpaceSelection}
            />

            <View alignItems="center" w="full">
                <Inline space="xxsmall">
                    {spaces.map((space, index) => {
                        const active = selectedSpace?.id === space.id;
                        const label = `${space.name}${active ? " selected." : "."} ${index + 1}/${
                            spaces.length
                        }`;

                        return (
                            <Box
                                key={space.id}
                                h={6}
                                w={active ? 12 : 6}
                                bgColor={active ? "white" : "rgba(255, 255, 255, 0.8)"}
                                borderRadius="full"
                                role="presentation"
                                aria-label={label}
                                aria-current={active ? "true" : "false"}
                            />
                        );
                    })}
                </Inline>
            </View>
        </Stack>
    ) : (
        <View alignItems="center">
            <HomeCustomButton onClick={() => handleSpaceChangerToggle(true)}>
                <Text size="xxsmall" color="white">
                    Show details
                </Text>
            </HomeCustomButton>
        </View>
    );
}

export const HomeBar = () => {
    const {spaces} = useSpaces();
    const {loading} = useAsyncLocalStorage(LocalStorageNames.SHOW_SPACE_CHANGER, true);

    const hasOnlyOneSpace = spaces.length === 1;
    const hasNoSpaces = spaces.length === 0;

    if (loading || hasNoSpaces) {
        return null;
    }

    return (
        <HomeGradient>
            <View w="100%" maxW={768} paddingLeft={["none", "none", "small-medium"]}>
                {hasOnlyOneSpace ? <SingleSpaceHeader /> : <MultiSpaceHeader />}
            </View>
        </HomeGradient>
    );
};
